/**
 * Configuración específica para fabricaciones.scm.web StorageIntegrityService
 * 
 * @version 1.1.0
 * @description Configuración de seguridad para localStorage en fabricaciones.scm.web, refactorizada para eliminar duplicación.
 * @author SCM Security Team
 * @since Sprint 64
 */

import { StorageIntegrityConfig } from './storage-integrity.interfaces';
import { EnvironmentService } from '../../services/environment.service';
import { inject } from '@angular/core';

/**
 * Crea la configuración base para un entorno específico (producción o desarrollo).
 * @param isProduction - Booleano que indica si el entorno es de producción.
 * @param secretKey - La clave secreta para la firma de datos.
 * @returns Un objeto de configuración de StorageIntegrity.
 */
function _createFabricacionesConfig(isProduction: boolean, secretKey: string): StorageIntegrityConfig {
  return {
    secretKey: secretKey,
    algorithm: isProduction ? 'sha512' : 'sha256',
    strictMode: isProduction,
    autoRecovery: !isProduction,
    keyPrefix: isProduction ? 'fabricaciones_scm_' : 'fabricaciones_scm_dev_',
    maxAge: isProduction ? 12 * 60 * 60 * 1000 : 24 * 60 * 60 * 1000, // 12h en prod, 24h en dev
    debug: !isProduction
  };
}

/**
 * Función para obtener la configuración basada en el entorno actual de Angular.
 * Utiliza inject() para una integración limpia y segura.
 */
export function getFabricacionesStorageConfig(): StorageIntegrityConfig {
  const envService = inject(EnvironmentService);
  const isProduction = envService.isProduction();
  const secretKey = envService.getSecretKey();
  
  return _createFabricacionesConfig(isProduction, secretKey);
}

/**
 * Configuración de fallback para cuando no hay contexto de Angular.
 * Detecta el entorno por hostname y usa claves inseguras temporales.
 */
export function getFallbackFabricacionesConfig(): StorageIntegrityConfig {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : 'localhost';
  const isProduction = !(hostname === 'localhost' || hostname === '127.0.0.1' || hostname.includes('.local'));
  const secretKey = isProduction ? 'prod-fallback-key-insecure' : 'dev-fallback-key-insecure';
  
  return _createFabricacionesConfig(isProduction, secretKey);
}

/**
 * Validación de configuración de seguridad.
 * Unifica la validación para cualquier contexto (Angular o fallback).
 */
export function validateFabricacionesSecurityConfig(): boolean {
  let currentConfig: StorageIntegrityConfig;
  
  try {
    // Prioriza la configuración inyectada por Angular
    currentConfig = getFabricacionesStorageConfig();
  } catch {
    // Si falla (fuera de contexto de inyección), usa el fallback
    currentConfig = getFallbackFabricacionesConfig();
  }
  
  if (!currentConfig.secretKey) {
    throw new Error('StorageIntegrity: Falta la clave de seguridad en la configuración.');
  }

  if (!currentConfig.keyPrefix) {
    throw new Error('StorageIntegrity: Falta el prefijo de clave en la configuración.');
  }

  if (currentConfig.secretKey.includes('fallback') || currentConfig.secretKey.includes('insecure')) {
    console.warn('⚠️ StorageIntegrity: Usando clave de fallback insegura. ¡Configura las variables de entorno!');
  }

  if (currentConfig.debug) {
    const environmentSource = currentConfig.secretKey.includes('fallback') ? 'detectado por hostname' : 'inyectado por EnvironmentService';
    console.log('🔐 Configuración de StorageIntegrity (Fabricaciones):', {
      algorithm: currentConfig.algorithm,
      keyPrefix: currentConfig.keyPrefix,
      debugMode: currentConfig.debug,
      strictMode: currentConfig.strictMode,
      maxAge: currentConfig.maxAge,
      environment: environmentSource
    });
  }

  return true;
}